import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import usePrice from 'src/hooks/usePrice';
import { setSelectedDisclaimer, openModal, setPrintActions, previewAccessory } from 'src/redux/configurator/actions';
import InfoCircle from 'src/assets/icons/info-circle.svg';
import Label from 'components/_utility/Label';
import OfferBanner from 'components/OfferBanner';
import useBikeTotal from 'src/hooks/useBikeTotal';
import useMobileDetect from 'src/hooks/useMobileDetect';
import {
  bikeModelSelector,
  bikeColourSelector,
  getSelectedServicePack,
  getSelectedWarrantyPack,
} from 'src/redux/bike/selectors';
import CalculatorIcon from 'src/assets/icons/calculator.svg';
import { removeServicePack, addServicePack, removeWarrantyPack, addWarrantyPack } from 'src/redux/bike/actions';
import { ctaSelector, getGoalId } from 'src/redux/app/selectors';
import { interceptLinkClick, isIOS, getLanguageCode, updateLinkSubmit, removeDuplicateParams } from 'helpers/utilities';
import useDataLayer from 'src/hooks/useDataLayer';
import useOpenFinance from 'src/hooks/useOpenFinance';
import useLaunchSendToDealer from 'src/hooks/useLaunchSendToDealer';
import { getConfigurations, getLoadedConfiguration, getSaving } from 'src/redux/garage/selectors';
import fetchGoal from 'helpers/goals';
import { useGeneratePDF } from 'src/hooks/useGeneratePDF';
import { getGarageEmailAddress } from 'helpers/garage';
import useSaveToGarage from 'src/hooks/useSaveToGarage';
import { getPrintActions, accessoryExtrasSelector } from 'src/redux/configurator/selectors';
import { getInteractions } from 'src/redux/tagging/selectors';
import { useNavigate } from 'helpers/routing';
import { getImageBaseUrl } from 'helpers/request';
import { setSaving } from 'src/redux/garage/actions';
import {
  Wrap,
  BikeTitle,
  TotalTitle,
  TotalWrap,
  Total,
  InfoCircleWrap,
  CalculateFinanceButton,
  Extras,
  ExtrasTitle,
  Extra,
  ExtraIconWrap,
  ExtraIcon,
  ExtraDetails,
  ExtraName,
  ExtraPrice,
  ExtraCTA,
  AdditionalCTAWrap,
  AdditionalCTA,
  Actions,
  Action,
  ActionIcon,
  ActionLabelWrap,
  PopupLabel,
} from './SummaryActions.styled';

const SummaryActions = ({ mobileOnly, desktopOnly, isSimulationChecksumExists, openShareModal }) => {
  const formatPrice = usePrice();
  const dispatch = useDispatch();
  const total = useBikeTotal();
  const pushToDataLayer = useDataLayer();
  const openFinance = useOpenFinance();
  const launchSendToDealer = useLaunchSendToDealer();
  const generatePDF = useGeneratePDF();
  const saveToGarage = useSaveToGarage();
  const navigate = useNavigate();
  const configurations = useSelector(getConfigurations);
  const interactions = useSelector(getInteractions);
  const loadedConfiguration = useSelector(getLoadedConfiguration);
  const bikeModel = useSelector(bikeModelSelector);
  const bikeColour = useSelector(bikeColourSelector);
  const ctaLinks = useSelector(ctaSelector);
  const garageSaving = useSelector(getSaving);
  // const sendToDealerData = useSelector(getSendToDealerData);
  const printActions = useSelector(getPrintActions);
  const extras = useSelector(accessoryExtrasSelector);
  const selectedServicePack = useSelector(getSelectedServicePack);
  const selectedWarrantyPack = useSelector(getSelectedWarrantyPack);
  const saveGoalId = useSelector(getGoalId('save configuration to garage'));
  const testRideGoalId = useSelector(getGoalId('cta - Test ride - summary page'));
  const printGoalId = useSelector(getGoalId('cta - print - summary page'));
  const feesTotal = extras?.fees?.reduce((prevTotal, fee) => prevTotal + Number(fee?.price || 0), 0);
  const totalPriceIncludingFees = total + feesTotal;

  const garageAction = loadedConfiguration
    ? {
        id: 'garage',
        label: 'garageLabel',
        icon: '/images/configurator/config-tray/Garage.svg',
        count: configurations.length,
        onClick: () => {
          pushToDataLayer('configuratorGarageAttempt');

          if (interactions.total) {
            return dispatch(openModal('unsavedConfiguration'));
          }

          if (!getGarageEmailAddress()) {
            return dispatch(openModal('loadGarage'));
          }

          navigate('/garage');
        },
      }
    : {
        id: 'save',
        label: 'saveToGarageLabel',
        icon: '/images/configurator/config-tray/Save.svg',
        onClick: async () => [
          pushToDataLayer('configuratorSaveAttempt'),
          getGarageEmailAddress() ? [await fetchGoal(saveGoalId), saveToGarage()] : dispatch(openModal('saveToGarage')),
        ],
        disabled: garageSaving,
      };

  const testBikeBaseUrl = ctaLinks.testRide?.includes('ColourCode')
    ? ctaLinks.testRide
    : ctaLinks.testRide + bikeModel.familyId;
  const bikeLegacy = bikeModel.canTestRide && ctaLinks.testRide ? testBikeBaseUrl : null;

  const testDriververride = bikeModel.testRideOverride;
  const testDriverButtonUrl = testDriververride ? testDriververride.url : bikeLegacy;

  const options = [
    garageAction,
    {
      id: 'testRide',
      label: 'requestTestRideLabel',
      icon: '/images/configurator/config-tray/Bike.svg',
      link: bikeModel.canTestRide
        ? updateLinkSubmit({
            link: testDriverButtonUrl,
            productCode: bikeModel?.productCode,
            colourCode: bikeColour?.colourCode,
            configurationID: loadedConfiguration?.id,
          })
        : null,
      onClick: async event => [
        await fetchGoal(testRideGoalId),
        interceptLinkClick(event, () => pushToDataLayer('configuratorTestRide')),
      ],
      hidden: !ctaLinks.testRide || !bikeModel.canTestRide,
    },
  ];

  const shareAndPrintOptions = [
    {
      id: 'share',
      label: 'shareLabel',
      icon: '/images/configurator/config-tray/Share.svg',
      onClick: () => [pushToDataLayer('configuratorShareAttempt'), openShareModal()],
    },
    {
      id: 'print',
      label: 'printLabel',
      icon: '/images/configurator/bike-image/printer.svg',
      onClick: () => [
        dispatch(setPrintActions(printActions + 1)),
        pushToDataLayer('configuratorPrint'),
        fetchGoal(printGoalId),
        generatePDF(),
      ],
    },
  ];

  const buyNowUrl = updateLinkSubmit({
    link: ctaLinks.buyNow,
    productCode: bikeModel?.productCode,
    colourCode: bikeColour?.colourCode,
    configurationID: loadedConfiguration?.id,
  });
  const primaryCTAOverride =
    bikeModel.configSummaryPrimaryCTAOverride &&
    updateLinkSubmit({
      link: bikeModel.configSummaryPrimaryCTAOverride.url,
      productCode: bikeModel?.productCode,
      colourCode: bikeColour?.colourCode,
      configurationID: loadedConfiguration?.id,
    });
  const primaryCTAOverrideText =
    bikeModel.configSummaryPrimaryCTAOverride && bikeModel.configSummaryPrimaryCTAOverride.text;

  const secondaryCTAOverride =
    bikeModel.configSummarySecondaryCTAOverride &&
    updateLinkSubmit({
      link: bikeModel.configSummarySecondaryCTAOverride.url,
      productCode: bikeModel?.productCode,
      colourCode: bikeModel?.colourCode,
      configurationID: loadedConfiguration?.id,
    });

  const secondaryCTAText = secondaryCTAOverride ? (
    bikeModel.configSummarySecondaryCTAOverride.text
  ) : (
    <Label name="buyNowLabel" />
  );

  const { configSummarySecondaryCTAOverride } = bikeModel;

  let secondaryCTAUrl = '';

  if (configSummarySecondaryCTAOverride) {
    secondaryCTAUrl = configSummarySecondaryCTAOverride.url;
  } else if (!buyNowUrl?.includes('ModelCode')) {
    secondaryCTAUrl = `${buyNowUrl}?bikeId=${bikeModel?.id}${
      bikeColour?.id && !loadedConfiguration ? `&colourId=${bikeColour?.id}` : ``
    }`;
  } else {
    secondaryCTAUrl = buyNowUrl;
  }

  if (loadedConfiguration?.id && !configSummarySecondaryCTAOverride) {
    secondaryCTAUrl += `&configurationId=${loadedConfiguration.id}&savedConfigurationUrl=${btoa(
      `${window.location.origin}/configure/bike/${bikeModel.id}/configure?configurationId=${loadedConfiguration.id}#config`
    )}`;
  }

  const [pushedExtra, setPushedExtra] = useState();

  useEffect(() => {
    if (!pushedExtra) {
      return;
    }

    pushToDataLayer(pushedExtra.isSelecting ? 'configuratorExtraSelected' : 'configuratorExtraRemoved', {
      configuratorExtraName: pushedExtra?.extra?.title,
      configuratorExtraPrice: pushedExtra?.extra?.price,
    });

    setPushedExtra();
  }, [pushedExtra]);

  const goToPagePrimary = id => {
    dispatch(setSaving(false));
    window.__configuratorRedirectTarget = true;
    const target = bikeModel.configSummaryPrimaryCTAOverride.target || '_self';
    window.open(
      removeDuplicateParams(
        updateLinkSubmit({
          link: primaryCTAOverride,
          productCode: bikeModel?.productCode,
          colourCode: bikeColour?.colourCode,
          configurationID: id || loadedConfiguration?.id,
          configurationUrl: `${window.location.origin}/configure/bike/${bikeModel.id}/configure?configurationId=${loadedConfiguration?.id}#config`,
        }),
        ['configurationID', 'configurationUrl', 'savedConfigurationUrl', 'configurationId']
      ),
      target
    );
  };

  const goToPageSecondary = id => {
    dispatch(setSaving(false));
    window.__configuratorRedirectTarget = true;
    const target = bikeModel?.configSummarySecondaryCTAOverride?.target || '_self';
    let redirectUrl = secondaryCTAUrl;
    const configId = id || loadedConfiguration?.id;

    if (secondaryCTAUrl?.includes('start-your-order') && configId) {
      const configUrl = btoa(
        `${window.location.origin}/configure/bike/${bikeModel?.id}/configure?configurationId=${id}#config`
      );
      redirectUrl += `&configurationId=${configId}&savedConfigurationUrl=${configUrl}`;
    }
    window.open(
      removeDuplicateParams(
        updateLinkSubmit({
          link:
            redirectUrl?.includes('ColourCode') && !redirectUrl?.includes('configurationID') && configId
              ? (redirectUrl += '&ConfigurationID={configurationID}&ConfigurationUrl={configurationUrl}')
              : redirectUrl,
          productCode: bikeModel?.productCode,
          colourCode: bikeColour?.colourCode,
          configurationID: configId,
          configurationUrl: `${window.location.origin}/configure/bike/${bikeModel.id}/configure?configurationId=${configId}#config`,
        }),
        ['configurationID', 'configurationUrl', 'savedConfigurationUrl', 'configurationId']
      ),
      target
    );
  };

  const checkFindOrSendToDealer = async () => {
    if (getGarageEmailAddress() && primaryCTAOverride) {
      dispatch(setSaving(true));
      saveToGarage(null, undefined, undefined, configurationId => goToPagePrimary(configurationId));

      return;
    }
    if (ctaLinks.dynamicsForm?.url) {
      window.open(ctaLinks.dynamicsForm.url, ctaLinks.dynamicsForm.target);
    } else if (!primaryCTAOverride) {
      launchSendToDealer(goToPagePrimary);
    } else {
      dispatch(
        openModal('saveToGarage', {
          afterSave: id => goToPagePrimary(id),
        })
      );
    }
  };

  const onSecondaryButton = async () => {
    if (getGarageEmailAddress()) {
      dispatch(setSaving(true));

      saveToGarage(null, undefined, undefined, configurationId => goToPageSecondary(configurationId));
    } else {
      dispatch(
        openModal('saveToGarage', {
          afterSave: id => goToPageSecondary(id),
        })
      );
    }
  };

  const handleSelectServicePack = id => {
    const selectedServiceExtra = extras.servicingPacks.find(service => service.id === id);

    if (selectedServiceExtra) {
      const isSelecting = selectedServicePack?.id !== selectedServiceExtra.id;

      dispatch(isSelecting ? addServicePack(selectedServiceExtra) : removeServicePack());

      setPushedExtra({
        isSelecting,
        extra: {
          configuratorExtraName: selectedServiceExtra?.title,
          configuratorExtraPrice: selectedServiceExtra?.price,
        },
      });
    }
  };

  const handleSelectWarrantyPack = id => {
    const selectedWarrantyExtra = extras.warrantyPacks.find(warranty => warranty.id === id);

    if (selectedWarrantyExtra) {
      const isSelecting = selectedWarrantyPack?.id !== selectedWarrantyExtra.id;

      dispatch(isSelecting ? addWarrantyPack(selectedWarrantyExtra) : removeWarrantyPack());

      setPushedExtra({
        isSelecting,
        extra: {
          configuratorExtraName: selectedWarrantyExtra?.title,
          configuratorExtraPrice: selectedWarrantyExtra?.price,
        },
      });
    }
  };

  const isMobile = useMobileDetect();

  const getSendToDealerLabel = () => {
    // if (!getGarageEmailAddress()) {
    //   return 'saveToGarageLabel';
    // }
    if (primaryCTAOverride) {
      return primaryCTAOverrideText;
    }
    if (ctaLinks.dynamicsForm?.url && ctaLinks.dynamicsForm?.text) {
      return ctaLinks.dynamicsForm.text;
    }
    return ctaLinks.findADealer ? 'findADealerLabel' : 'sendToDealerLabel';
  };

  const secondaryButton = useMemo(
    () => (
      <AdditionalCTA
        as="button"
        onClick={e => {
          onSecondaryButton(e, true, false);
          pushToDataLayer('configuratorSumSec');
          pushToDataLayer('configuratorGarageBuyNow');
        }}
      >
        {secondaryCTAText}
      </AdditionalCTA>
    ),
    []
  );

  return (
    <Wrap mobileOnly={mobileOnly} desktopOnly={desktopOnly}>
      {isMobile &&
        options
          .filter(option => !option.hidden)
          .map(({ ...option }) => {
            const linkProps = {};

            if (option.link) {
              linkProps.target = '_blank';
              linkProps.href = option.link;
            } else {
              linkProps.as = 'button';
            }

            if (option.onClick) {
              linkProps.onClick = option.onClick;
            }

            if (option.disabled) {
              linkProps.disabled = option.disabled;
            }

            return (
              <Action key={option.id} {...linkProps}>
                <ActionIcon src={option.icon}></ActionIcon>
                <ActionLabelWrap>
                  <Label name={option.label} />
                </ActionLabelWrap>
              </Action>
            );
          })}
      {isMobile && ctaLinks.buyNow && !bikeModel.configSummaryHideSecondaryCTA ? secondaryButton : null}
      <BikeTitle dangerouslySetInnerHTML={{ __html: bikeModel.name }}></BikeTitle>
      <TotalTitle>
        <Label name="otrPriceLabel" />
        <InfoCircleWrap
          onClick={() => {
            dispatch(setSelectedDisclaimer('price'));
            dispatch(openModal('disclaimer'));
          }}
        >
          <InfoCircle />
        </InfoCircleWrap>
      </TotalTitle>
      <TotalWrap>
        <Total>{formatPrice(totalPriceIncludingFees)}</Total>
        {ctaLinks.finance && bikeModel.canFinance && isSimulationChecksumExists ? (
          <CalculateFinanceButton onClick={() => [pushToDataLayer('configuratorSumFinance'), openFinance()]}>
            <CalculatorIcon></CalculatorIcon>
            <Label name="calculateFinanceLabel"></Label>
            <i aria-hidden="true" className="icon icon--arrow-right"></i>
          </CalculateFinanceButton>
        ) : null}
      </TotalWrap>
      {extras ? (
        <Extras>
          {extras.servicingPacks.length || extras.warrantyPacks.length ? (
            <ExtrasTitle>
              <Label name="extrasLabel"></Label>
            </ExtrasTitle>
          ) : null}
          {extras.servicingPacks
            ? extras.servicingPacks.map(servicePack => {
                const selected = selectedServicePack?.id === servicePack.id;

                return (
                  <Extra key={servicePack.id}>
                    <ExtraIconWrap>
                      <ExtraIcon
                        src={getImageBaseUrl() + servicePack.image.srcSmall}
                        alt={servicePack.image.alt}
                      ></ExtraIcon>
                    </ExtraIconWrap>
                    <ExtraDetails>
                      <ExtraName>
                        {servicePack.title}{' '}
                        <InfoCircleWrap
                          data-testid="infoCircle"
                          onClick={event => [
                            event.stopPropagation(),
                            event.preventDefault(),
                            pushToDataLayer('configuratorExtraInfo', {
                              configuratorExtraName: servicePack.title,
                              configuratorExtraPrice: servicePack.price,
                            }),
                            dispatch(openModal('accessories')),
                            dispatch(
                              previewAccessory({
                                ...servicePack,
                                images: [{ src: servicePack.image.srcLarge }],
                              })
                            ),
                          ]}
                        >
                          <InfoCircle />
                        </InfoCircleWrap>
                      </ExtraName>
                      <ExtraPrice>
                        {servicePack.price ? formatPrice(Number(servicePack.price)) : servicePack.priceText}
                      </ExtraPrice>
                    </ExtraDetails>
                    <ExtraCTA selected={selected} onClick={() => handleSelectServicePack(servicePack.id)}>
                      <Label name={selected ? 'selectedLabel' : 'selectLabel'}></Label>
                    </ExtraCTA>
                  </Extra>
                );
              })
            : null}
          {extras.warrantyPacks
            ? extras.warrantyPacks.map(warrantyPack => {
                const selected = selectedWarrantyPack?.id === warrantyPack.id;

                return (
                  <Extra key={warrantyPack.id}>
                    <ExtraIconWrap>
                      <ExtraIcon
                        src={getImageBaseUrl() + warrantyPack.image.srcSmall}
                        alt={warrantyPack.image.alt}
                      ></ExtraIcon>
                    </ExtraIconWrap>
                    <ExtraDetails>
                      <ExtraName>
                        {warrantyPack.title}{' '}
                        <InfoCircleWrap
                          data-testid="infoCircle"
                          onClick={event => [
                            event.stopPropagation(),
                            event.preventDefault(),
                            pushToDataLayer('configuratorExtraInfo', {
                              configuratorExtraName: warrantyPack.title,
                              configuratorExtraPrice: warrantyPack.price,
                            }),
                            dispatch(openModal('accessories')),
                            dispatch(
                              previewAccessory({
                                ...warrantyPack,
                                images: [{ src: warrantyPack.image.srcLarge }],
                              })
                            ),
                          ]}
                        >
                          <InfoCircle />
                        </InfoCircleWrap>
                      </ExtraName>
                      <ExtraPrice>
                        {warrantyPack.price ? formatPrice(Number(warrantyPack.price)) : warrantyPack.priceText}
                      </ExtraPrice>
                    </ExtraDetails>
                    <ExtraCTA selected={selected} onClick={() => handleSelectWarrantyPack(warrantyPack.id)}>
                      <Label name={selected ? 'selectedLabel' : 'selectLabel'}></Label>
                    </ExtraCTA>
                  </Extra>
                );
              })
            : null}
          {extras.fees.length ? (
            <>
              <ExtrasTitle>
                <Label name="feesLabel"></Label>
              </ExtrasTitle>
              {extras.fees.map(fee => {
                const selected = true;

                return (
                  <Extra key={fee.id}>
                    <ExtraIconWrap>
                      <ExtraIcon src={getImageBaseUrl() + fee.image.srcSmall} alt={fee.image.alt}></ExtraIcon>
                    </ExtraIconWrap>
                    <ExtraDetails>
                      <ExtraName>
                        {fee.title}{' '}
                        <InfoCircleWrap
                          data-testid="infoCircle"
                          onClick={event => {
                            event.stopPropagation();
                            event.preventDefault();
                            pushToDataLayer('configuratorExtraInfo', {
                              configuratorExtraName: fee.title,
                              configuratorExtraPrice: fee.price,
                            });
                            dispatch(openModal('accessories'));
                            dispatch(
                              previewAccessory({
                                ...fee,
                                images: [{ src: fee.image.srcLarge }],
                                feeDisclaimer: true,
                              })
                            );
                          }}
                        >
                          <InfoCircle />
                        </InfoCircleWrap>
                      </ExtraName>
                      <ExtraPrice>{fee.price ? formatPrice(Number(fee.price)) : fee.priceText}</ExtraPrice>
                    </ExtraDetails>
                    <ExtraCTA selected={selected} disabled>
                      <Label name="selectedLabel"></Label>
                    </ExtraCTA>
                  </Extra>
                );
              })}
            </>
          ) : null}
        </Extras>
      ) : null}
      {bikeModel.offerSummaryContent ? <OfferBanner offerBanner={bikeModel.offerSummaryContent}></OfferBanner> : null}
      <AdditionalCTAWrap>
        <AdditionalCTA
          as="button"
          onClick={() => [pushToDataLayer('configuratorSumPrim'), checkFindOrSendToDealer()]}
          primary
        >
          <Label name={getSendToDealerLabel()} />
        </AdditionalCTA>
        {bikeModel.canTestRide && !ctaLinks.buyNow ? (
          <AdditionalCTA
            href={updateLinkSubmit({
              link: testDriverButtonUrl,
              productCode: bikeModel?.productCode,
              colourCode: bikeColour?.colourCode,
            })}
            onClick={async event => [
              await fetchGoal(testRideGoalId),
              interceptLinkClick(event, () => {
                pushToDataLayer('configuratorTestRide');
                pushToDataLayer('configuratorSumSec');
              }),
            ]}
            target="_blank"
          >
            <Label name="testRideLabel" />
          </AdditionalCTA>
        ) : null}
        {!isMobile && ctaLinks.buyNow && !bikeModel.configSummaryHideSecondaryCTA ? secondaryButton : null}
      </AdditionalCTAWrap>
      <Actions>
        {!isMobile &&
          options
            .filter(option => !option.hidden)
            .map(({ ...option }) => {
              const linkProps = {};

              if (option.link) {
                linkProps.target = '_blank';
                linkProps.href = option.link;
              } else {
                linkProps.as = 'button';
              }

              if (option.onClick) {
                linkProps.onClick = option.onClick;
              }

              if (option.disabled) {
                linkProps.disabled = option.disabled;
              }

              return (
                <Action key={option.id} {...linkProps}>
                  <ActionIcon src={option.icon}></ActionIcon>
                  <ActionLabelWrap>
                    <Label name={option.label} />
                  </ActionLabelWrap>
                </Action>
              );
            })}
        {shareAndPrintOptions
          .filter(option => !option.hidden)
          .map(({ ...option }) => {
            const linkProps = {};
            const isZhLanguage = getLanguageCode()
              ?.toLowerCase()
              ?.startsWith('zh');
            if (isZhLanguage && option.id === 'print') {
              return null;
            }

            if (option.link) {
              linkProps.target = '_blank';
              linkProps.href = option.link;
            } else {
              linkProps.as = 'button';
            }

            if (option.onClick) {
              linkProps.onClick = option.onClick;
            }

            if (option.disabled) {
              linkProps.disabled = option.disabled;
            }

            return (
              <Action key={option.id} {...linkProps}>
                <ActionIcon src={option.icon}></ActionIcon>
                <ActionLabelWrap>
                  <Label name={option.label} />
                </ActionLabelWrap>
              </Action>
            );
          })}
        {printActions && isIOS() ? (
          <PopupLabel>
            <Label name="printGuidanceLabel" />{' '}
            <a href="App-prefs://prefs:root=SAFARI">
              <Label name="printGuidanceCta" />
            </a>
          </PopupLabel>
        ) : null}
      </Actions>
    </Wrap>
  );
};

export default SummaryActions;
